import axios, {isCancel, AxiosRequestConfig} from 'axios';
import {oktaAuth, generateNewToken, isTokenExpired} from './AuthService';
const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

interface RetryConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

const apiClient = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
});

apiClient.interceptors.request.use(
  async (config) => {
    // if (config.url?.includes('test-500-error')) {
    // 	console.log('--- SIMULATING 500 ERROR ---');
    // 	return Promise.reject({
    // 		config: config,
    // 		response: {status: 500, data: 'Simulated server error'},
    // 	});
    // }

    // // --- Use this block to test a 401 error ---
    // if (config.url?.includes('test-401-error')) {
    // 	console.log('--- SIMULATING 401 ERROR ---');
    // 	return Promise.reject({
    // 		config: config,
    // 		response: {status: 401, data: 'Simulated token error'},
    // 	});
    // }
    // Callers that already set their own Authorization header (e.g. an
    // exchanged OIDC token) should not be overridden with the Okta token.
    if (config.headers?.Authorization) {
      return config;
    }
    const tokenObj: any = await oktaAuth.tokenManager.get('accessToken');
    let token = tokenObj?.accessToken;
    if (isTokenExpired(token)) {
      token = await generateNewToken();
    }
    // Safer way to set the header
    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
apiClient.interceptors.response.use(
  (response) => {
    response.status = 200;
    return response;
  },
  async (error) => {
    const originalRequest: RetryConfig = error.config;
    const {response} = error;

    // --- Logic for 403 Forbidden Error ---
    // If the error is 403, and we haven't already retried this request
    if (
      response &&
      (response.status === 401 || response.status === 403) &&
      !originalRequest._retryCount
    ) {
      console.log('Token expired or invalid. Attempting to refresh...');
      originalRequest._retryCount = 1; // Mark that we have retried once

      try {
        const newToken = await generateNewToken();
        //replacing original request with the new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        // Retry the original request with the new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, reject with the refresh error
        return Promise.reject(refreshError);
      }
    }

    // --- Logic for 500 Internal Server Error ---
    if (response && response.status >= 500 && response.status < 600) {
      originalRequest._retryCount = originalRequest._retryCount || 0;
      // Limit retries to 3 times to avoid infinite loops
      if (originalRequest._retryCount < 3) {
        originalRequest._retryCount++;
        console.log(
          `Request failed with ${response.status}. Retrying (${originalRequest._retryCount}/3)...`,
        );
        // Create a exponential delay
        const delayTime = 2 ** originalRequest._retryCount * 1000;
        const delay = new Promise((resolve) => setTimeout(resolve, delayTime));
        await delay;

        // Retry the request
        return apiClient(originalRequest);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;

export {isCancel};
